import { unwrapResult } from "@reduxjs/toolkit";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import forwardarrow from "../../../../assets/images/arrow_forwardb.svg";
import goldIcon from "../../../../assets/images/Gold.svg";
import platiniumIcon from "../../../../assets/images/Platinium.svg";
import silverIcon from "../../../../assets/images/Silver.svg";
import Button from "../../../../components/common/Button/Button";
import TitleDescription from "../../../../components/newMotorPolicy/titleDescription/titleDescription";
import SummaryContent from "../../../../components/common/SummaryContent/SummaryContent";
import useLanguageTranslate from "../../../../hooks/useLanguageTranslate";
import useTravelChangePlanFlow from "../../../../hooks/useTravelChangePlanFlow";
import { PolicyViewResponse } from "../../../../redux/features/motor/getQuoteSlice";
import { selectTravelFormData } from "../../../../redux/features/newTravelFormSlice";
import {
  getPolicyPayLoading,
  policyPayAddCovers,
} from "../../../../redux/features/payment/policyPaySlice";
import { getClientSource } from "../../../../utils/getClientSource";
import { applyPromoCodeToQuote, calculatePromoDiscount } from "../../../../utils/promoCodeUtils";
import { TRAVEL_CHANGE_PLAN_SELECT_PLAN } from "../../../motorInsurancePages/newMotorPolicy/Layouts/BottomStickyBtnLayout";
import SelectNewTravelPlanLayout from "./selectNewTravelPlanLayout";

export const TRAVEL_CHANGE_PLAN_ENHANCEMENT = "TRAVEL_CHANGE_PLAN_ENHANCEMENT";

// Create a mapping of travel plan code to image and name (like motor insurance)
// Old codes (SILVER/GO/PL) kept for existing policies, new codes for new travel plans
const travelPlanMap = {
  SILVER: {
    image: silverIcon,
    text: "Silver",
    text_ar: "الفضية",
  },
  GO: {
    image: goldIcon,
    text: "Gold",
    text_ar: "الذهبية",
  },
  PL: {
    image: platiniumIcon,
    text: "Platinum",
    text_ar: "البلاتينية",
  },
  SCHENGENB: {
    image: silverIcon,
    text: "Schengen",
    text_ar: "شنغن",
  },
  WLEXB: {
    image: goldIcon,
    text: "Worldwide (Excl. USA & Canada)",
    text_ar: "جميع دول العالم (باستثناء أمريكا وكندا)",
  },
  WLB: {
    image: platiniumIcon,
    text: "Worldwide",
    text_ar: "جميع دول العالم",
  },
};

const ReviewAndConfirmTravelPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [promoCodeApplied, setPromoCodeApplied] = useState(false);
  const [promoCodeDiscount, setPromoCodeDiscount] = useState(0);
  const [isApplyingPromo, setIsApplyingPromo] = useState(false);
  const [promoAppliedQuoteData, setPromoAppliedQuoteData] = useState(null);
  const { policy_id } = useParams();

  // Add travel change plan flow for back button
  const { handlePrevSubstep, handlePrevStep } = useTravelChangePlanFlow();

  const policyPayLoading = useSelector(getPolicyPayLoading);
  const policyInfo = useSelector(PolicyViewResponse);

  // Get selected plan from Redux (using travel form Redux instead of motor form)
  const selectedPlan =
    useSelector(selectTravelFormData)?.[TRAVEL_CHANGE_PLAN_SELECT_PLAN] || {};

  console.log("📋 Review page - selected plan from Redux:", selectedPlan);
  console.log("📋 Review page - policyInfo:", policyInfo);
  console.log(
    "📋 Review page - currentPlanCode:",
    policyInfo?.PolicyHeader?.PlanCode,
  );
  console.log("📋 Review page - newPlanCode:", selectedPlan?.code);

  // Determine if this is an upgrade or downgrade
  const currentPlanCode = policyInfo?.PolicyHeader?.PlanCode;
  const newPlanCode = selectedPlan?.code;

  // Travel plan hierarchy: old codes (SILVER < GO < PL) and new codes (SCHENGENB < WLEXB < WLB)
  const planHierarchy = { SILVER: 1, GO: 2, PL: 3, SCHENGENB: 1, WLEXB: 2, WLB: 3 };
  const currentPlanWeight = planHierarchy[currentPlanCode] || 0;
  const newPlanWeight = planHierarchy[newPlanCode] || 0;
  const isUpgrade = newPlanWeight > currentPlanWeight;

  // Calculate pricing
  const premium = selectedPlan.premium || selectedPlan.totalPremium || 0;
  const tax = selectedPlan.tax || premium * 0.1; // 10% VAT
  const totalPrice = selectedPlan.totalPremium || premium + tax;

  // Calculate VAT as 10% of the total price
  const VAT = totalPrice * 0.1;

  console.log(
    "on travel change plan payment page, selected plan: ",
    selectedPlan,
  );
  console.log("on travel change plan payment page, policy info: ", policyInfo);

  const declaration = t("motorInsurance.addMoreCovers.declaration", {
    returnObjects: true,
  });

  const applyPromoCode = async () => {
    const quoteNum = policyInfo?.PolicyHeader?.PolicyNumber;
    setIsApplyingPromo(true);
    const result = await applyPromoCodeToQuote(quoteNum, promoCode, translate);
    if (result.success) {
      setPromoAppliedQuoteData(result.data);
      setPromoCodeApplied(true);
      setPromoCodeDiscount(calculatePromoDiscount(totalPrice, result.data.TotalPremium));
    } else {
      toast.error(result.error);
    }
    setIsApplyingPromo(false);
  };

  const clearPromoCode = () => {
    setPromoCode("");
    setPromoCodeApplied(false);
    setPromoCodeDiscount(0);
    setPromoAppliedQuoteData(null);
  };

  // Build planData for SummaryContent
  const planData = {
    basePremium: premium,
    premium: premium,
    tax: VAT,
    totalPremium: totalPrice,
    quoteNumber: policyInfo?.PolicyHeader?.PolicyNumber || "N/A",
    policyNumber: policyInfo?.PolicyHeader?.PolicyNumber || "N/A",
    Covers: [],
    getCoverPriceByCode: () => 0,
  };

  const handlePayment = async () => {
    const finalQuoteNumber = promoCodeApplied && promoAppliedQuoteData ? promoAppliedQuoteData.PolicyNumber : (policyInfo?.PolicyHeader?.PolicyNumber ?? "");
    const finalTotal = promoCodeApplied && promoAppliedQuoteData ? promoAppliedQuoteData.TotalPremium : totalPrice;
    const payload = {
      PolicyNumber: finalQuoteNumber,
      Premium: finalTotal ?? "",
      Channel: getClientSource(),
      NewPlan: selectedPlan.code ?? "",
      uatAmount: `${totalPrice}` ?? "",
    };

    try {
      console.log("Final selected travel plan:", selectedPlan);
      // For travel change plan, we might need a different API endpoint
      // Using the motor one for now, but this might need to be updated
      const linkAction = await dispatch(policyPayAddCovers(payload));
      const link = unwrapResult(linkAction);

      if (link) {
        window.open(link, "_self");
      }
    } catch (error) {
      toast.error("Error in getting payment link: ", error);
    }
  };

  return (
    <SelectNewTravelPlanLayout
      backClickHandler={handlePrevStep}
      is_bottom_sticky_layout={false}
      show_summary={false}
      btn_onClick={handlePayment}
    >
      <div className="flex flex-col items-center mx-auto max-w-lg max767:p-4">
        <div className="flex justify-center items-center w-full">
          <div className="p-0 w-full">
            <div className="max767:mt-2">
              <TitleDescription
                title={translate("Review and Confirm", "المراجعة والتأكيد")}
                description={translate(
                  "Please review your information and proceed to payment.",
                  "يرجى مراجعة معلوماتك والانتقال إلى الدفع.",
                )}
              />
            </div>

            <div className="mt-10">
              <div className="bg-[#F6F8F9] mb-6 p-4 max767:bg-white max767:p-0 rounded-2xl">
                <h4 className="text-[#48535B] rtl:max767:text-[#1A2024] text-base not-italic font-semibold leading-6 tracking-[-0.16px] mb-4">
                  {isUpgrade
                    ? translate(
                        "You are upgrading your plan",
                        "أنت تقوم بترقية خطتك",
                      )
                    : translate("Change Plan", "تغيير الخطة")}
                </h4>

                {/* Current Plan */}
                <div className="plan flex align-middle items-center gap-5">
                  <img
                    src={travelPlanMap[currentPlanCode]?.image || silverIcon}
                    className="items-center p-2"
                    alt={translate("Current plan", "الخطة الحالية")}
                  />
                  <p className="text-[#48535B] text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] rtl:max767:text-[#1A2024]">
                    {translate(
                      travelPlanMap[currentPlanCode]?.text || "Current Plan",
                      travelPlanMap[currentPlanCode]?.text_ar ||
                        "الخطة الحالية",
                    )}
                  </p>
                </div>
                <div className="line h-6 border-l-2 ml-8 rtl:border-l-0 rtl:ml-0 rtl:border-r-2 rtl:mr-8"></div>

                {/* New Plan */}
                <div className="plan flex align-middle gap-5 mb-5 items-center">
                  <img
                    src={
                      travelPlanMap[newPlanCode]?.image ||
                      selectedPlan?.imgSrc ||
                      platiniumIcon
                    }
                    className="items-center p-2"
                    alt={translate("New plan", "الخطة الجديدة")}
                  />
                  <p className="text-[#1A2024] text-2xl not-italic font-semibold leading-8 tracking-[-0.24px] rtl:max767:text-[#48535B]">
                    {translate(
                      travelPlanMap[newPlanCode]?.text ||
                        selectedPlan?.name ||
                        "New Plan",
                      travelPlanMap[newPlanCode]?.text_ar ||
                        selectedPlan?.name_ar ||
                        "الخطة الجديدة",
                    )}
                  </p>
                </div>

                {/* Policy Details Table */}
                <div className="max767:overflow-x-scroll">
                  <table className="min-w-full border-separate bg-white border border-solid border-[#DDE2E4] border-spacing-0 rounded-[10px]">
                    <tbody>
                      {/* Passengers Section */}
                      {/* Passenger 1 is always the policy holder (Party) — full info. */}
                      <tr className="border-b">
                        <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                          {translate("Passenger 1", "المسافر ١")}
                        </td>
                        <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                          {`${policyInfo?.PolicyHeader?.Party?.FirstName || ""} ${
                            policyInfo?.PolicyHeader?.Party?.LastName || ""
                          }`.trim() || "N/A"}
                        </td>
                      </tr>
                      {/* Family plans: additional members from the Passengers list.
                          Skip empty/placeholder entries (no name) so individual
                          plans don't show a stray "Passenger 2 — N/A". */}
                      {(policyInfo?.PolicyHeader?.Passengers || [])
                        .map(
                          (passenger) =>
                            passenger?.name ||
                            passenger?.fullName ||
                            `${passenger?.FirstName || ""} ${
                              passenger?.LastName || ""
                            }`.trim(),
                        )
                        .filter(Boolean)
                        .map((name, index) => (
                          <tr key={index} className="border-b">
                            <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                              {translate(
                                `Passenger ${index + 2}`,
                                `المسافر ${index + 2}`,
                              )}
                            </td>
                            <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                              {name}
                            </td>
                          </tr>
                        ))}

                      <tr className="border-b">
                        <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                          {translate("Departure date", "تاريخ المغادرة")}
                        </td>
                        <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                          {policyInfo?.PolicyHeader?.FromDateStr}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                          {translate("Return date", "تاريخ العودة")}
                        </td>
                        <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                          {policyInfo?.PolicyHeader?.ToDateStr}
                        </td>
                      </tr>
                      <tr className="border-b">
                        <td className="py-2 px-4 border-b border-r rtl:border-l text-[#5B6871] text-sm not-italic font-medium leading-6 h-[44px]">
                          {translate("Plan", "الخطة")}
                        </td>
                        <td className="py-2 px-4 border-b text-[#1A2024] text-sm not-italic font-medium leading-6">
                          {translate(
                            travelPlanMap[newPlanCode]?.text ||
                              selectedPlan?.name ||
                              "New Plan",
                            travelPlanMap[newPlanCode]?.text_ar ||
                              selectedPlan?.name_ar ||
                              "الخطة الجديدة",
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mb-8 w-full">
          <div className="bg-[#F6F8F9] rounded-xl p-4">
            <SummaryContent
              planData={promoCodeApplied && promoAppliedQuoteData ? { ...planData, premium: promoAppliedQuoteData.Premium ?? planData.premium, tax: promoAppliedQuoteData.Tax ?? planData.tax, totalPremium: promoAppliedQuoteData.TotalPremium ?? planData.totalPremium, quoteNumber: promoAppliedQuoteData.PolicyNumber ?? planData.quoteNumber } : planData}
              promoCode={promoCode}
              setPromoCode={setPromoCode}
              applyPromoCode={applyPromoCode}
              clearPromoCode={clearPromoCode}
              promoCodeApplied={promoCodeApplied}
              promoCodeDiscount={promoCodeDiscount}
              isApplyingPromo={isApplyingPromo}
              showQuotationNumber={true}
              showPromoCode={true}
              className="!bg-transparent"
              flowType="travel"
            />
          </div>
        </div>

        {/* Policy Notice */}
        <div className="mb-6 p-4 bg-[#FDE9D2] border border-[#FBC98E] rounded-lg flex items-start space-x-3 rtl:space-x-reverse w-full">
          <div className="flex-shrink-0 mt-0.5">
            <svg
              className="w-5 h-5 text-[#A56213]"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div>
            <p className="text-[#252C32] font-semibold text-sm">
              {translate(
                "Kindly note that the policy will be issued under your name",
                "يرجى ملاحظة أن الوثيقة ستصدر باسمك"
              )}
            </p>
          </div>
        </div>

        {/* Terms and Conditions */}
        <div className="mb-6 w-full max767:!pb-20">
          <label className="flex items-start space-x-3 rtl:space-x-reverse cursor-pointer">
            <label className="flex items-center cursor-pointer gap-2">
              <input
                type="checkbox"
                className="peer hidden"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
              />
              <div
                className="size-5 border-2 border-gray-400 rounded-[4px]
                  peer-checked:bg-[#00A9E7] peer-checked:border-[#00A9E7]
                  flex items-center justify-center transition-all"
              >
                <svg
                  width="12"
                  height="9"
                  viewBox="0 0 12 9"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M11 0C10.72 0 10.47 0.11 10.29 0.29L4 6.59L1.71 4.29C1.53 4.11 1.28 4 1 4C0.45 4 0 4.45 0 5C0 5.28 0.11 5.53 0.29 5.71L3.29 8.71C3.47 8.89 3.72 9 4 9C4.28 9 4.53 8.89 4.71 8.71L11.71 1.71C11.89 1.53 12 1.28 12 1C12 0.45 11.55 0 11 0Z"
                    fill="white"
                  />
                </svg>
              </div>
            </label>
            <div className="text-sm text-[#252C32] rtl:font-medium leading-[24px] text-justify">
              <span>
                {translate(
                  "I hereby certify that the above given information is true and correct to the best of my knowledge, and I agree to cooperate fully by providing all requested documents before payment. I understand that providing invalid information will result in my insurance policy to be void. I also read and agree the",
                  "أشهد بموجب هذا أن المعلومات المقدمة أعلاه صحيحة ودقيقة حسب أفضل ما أعلم، وأوافق على التعاون الكامل من خلال تقديم جميع الوثائق المطلوبة قبل الدفع. أفهم أن تقديم معلومات غير صحيحة سيؤدي إلى إبطال وثيقة التأمين الخاصة بي. كما أنني قرأت وأوافق على"
                )}
              </span>{" "}
              <button
                onClick={() =>
                  window.open(
                    "https://content.solidarity.bh/uploads/TRAVEL_INSURANCE_WORDING_30_08_2023_8e74afd369_44e9596879.pdf",
                    "_blank"
                  )
                }
                className="text-[#00384D] underline hover:text-[#0088CC] font-bold"
              >
                {translate("Policy Specification", "مواصفات وثيقة")}
              </button>{" "}
              <span>
                {translate("of this Insurance product.", "التأمين هذه.")}
              </span>
            </div>
          </label>
        </div>

        {/* Moved outside the grey box */}
        <div className="max767:footer max767:bg-white max767:border-t-[#E5E9EB] max767:border-t max767:border-solid max767:mt-2 max767:w-full max767:py-2 flex max767:flex-col !gap-4 mt-4 max767:fixed max767:bottom-0 max767:w-full max767:p-4 max767:inset-x-0 ipad:items-center max767:inline-block max767:z-[9] w-full">
          <Button
            isLoading={policyPayLoading}
            disabled={!agreedToTerms}
            className="bg-[#55C6EF] mt-4 mb-9 max767:mb-4 w-full text-[#48535B] text-lg not-italic font-semibold leading-6 tracking-[-0.18px] flex justify-center p-3 rounded-[10px] cursor-pointer w-full"
            label={translate("Proceed to Payment", "انتقل إلى الدفع")}
            onSubmit={handlePayment}
            post_icon={forwardarrow}
            post_icon_class={"w-6 h-6 ml-[5px] rtl:rotate-180 rtl:mr-2"}
            fontSize="text-lg max767:text-sm"
          />
        </div>
      </div>
    </SelectNewTravelPlanLayout>
  );
};

export default ReviewAndConfirmTravelPage;
